import { z } from "zod";

/**
 * Le contrat de l'autorisation des comptes Twitch (la chaine et le bot), partage entre les routes
 * `/api/twitch/*` du backend (`packages/backend/src/app/twitch/routes.ts`) et la page `/regie` du
 * frontend. Les deux cotes le lisent par leur pont `src/app/contract.ts`.
 *
 * Il vit ici, et non dans `contract.ts` de ce paquet, parce que `src/contract.ts` appartient au
 * template : l'app possede `src/app/**`.
 */

/**
 * Les codes que `GET /api/twitch/retour` met dans l'adresse de la regie (`/regie?twitch=<code>`).
 * Une liste fermee, ecrite ici : jamais un morceau de ce que Twitch a repondu.
 * - `ok` : le compte est enregistre ;
 * - `refus` : l'utilisateur a refuse l'autorisation chez Twitch (tout parametre `error`) ;
 * - `etat_invalide` : etat inconnu, deja consomme, trop vieux, ou `code` absent ;
 * - `code_refuse` : Twitch a refuse l'echange du code (code perime ou rejoue, ou configuration) ;
 * - `mauvais_compte` : le compte autorise n'a pas le login attendu pour ce role ;
 * - `droits_manquants` : la validation du jeton montre un droit demande en moins ;
 * - `indisponible` : Twitch, la base ou la configuration ne permettent pas de conclure ; on refait.
 */
export const RETURN_CODES = [
  "ok",
  "refus",
  "etat_invalide",
  "code_refuse",
  "mauvais_compte",
  "droits_manquants",
  "indisponible",
] as const;
export type ReturnCode = (typeof RETURN_CODES)[number];

/** Un compte Twitch relie, tel que `GET /api/twitch/etat` le rend : jamais un jeton. */
export const accountStatusSchema = z.object({
  role: z.enum(["chaine", "bot"]),
  login: z.string(),
  nom: z.string(),
  etat: z.enum(["ok", "a_reautoriser"]),
  erreur: z.string().nullable(),
  scopesManquants: z.array(z.string()),
  autoriseLe: z.string(),
  valideLe: z.string().nullable(),
});
export type TwitchAccount = z.infer<typeof accountStatusSchema>;

/**
 * Pourquoi le travail de fond est dans l'etat ou il est (`RuntimeStatus.raison`). Une liste fermee,
 * ecrite par l'app : la regie a une phrase pour chacune.
 * - `ok` : la connexion a Twitch tourne (ou se reprend d'elle-meme apres une coupure) ;
 * - `non_configure` : une variable de Twitch manque, rien n'est tente ;
 * - `verrou` : une autre instance de l'app tient la connexion (ou la base n'a pas repondu) ;
 * - `compte_absent` : le compte de la chaine n'est pas autorise, ou doit l'etre de nouveau ;
 * - `en_pause` : Hermes a mis l'app en pause ou en maintenance, la connexion est fermee ;
 * - `arrete` : le travail de fond n'a pas demarre, ou l'app s'arrete.
 */
export const RUNTIME_REASONS = [
  "ok",
  "non_configure",
  "verrou",
  "compte_absent",
  "en_pause",
  "arrete",
] as const;
export type RuntimeReason = (typeof RUNTIME_REASONS)[number];

/**
 * L'etat de la connexion EventSub. `depuis` est une date ISO 8601 : pour `connecte`, le debut de la
 * session ; pour `coupe`, la coupure qui a commence la panne en cours. `tentatives` compte les
 * connexions tentees depuis cette coupure.
 */
export const eventSubStateSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("arrete") }),
  z.object({ kind: z.literal("connexion") }),
  z.object({ kind: z.literal("connecte"), depuis: z.string() }),
  z.object({
    kind: z.literal("coupe"),
    depuis: z.string(),
    tentatives: z.number().int().nonnegative(),
  }),
]);
export type EventSubStatus = z.infer<typeof eventSubStateSchema>;

/**
 * Ce que le travail de fond dit de lui-meme (`status()` de `runtime.ts`). `actif` est vrai dans
 * l'etat `ok`. `dernierReleveLe` est la date du dernier releve de presence reussi, `null` avant le
 * premier. `evenementsIgnores` compte, depuis le demarrage du processus, les evenements que le
 * journal a refuses de lire : un format de Twitch qui change se voit ici. `abonnementsRefuses` liste
 * les types d'abonnement (noms de l'app, jamais un texte de Twitch) que Twitch a refuses a la
 * derniere session : les autres fonctionnent, la connexion reste ouverte.
 */
export const runtimeStatusSchema = z.object({
  actif: z.boolean(),
  raison: z.enum(RUNTIME_REASONS),
  eventsub: eventSubStateSchema,
  dernierReleveLe: z.string().nullable(),
  evenementsIgnores: z.number().int().nonnegative(),
  abonnementsRefuses: z.array(z.string()),
});
export type RuntimeStatus = z.infer<typeof runtimeStatusSchema>;

/** Un evenement du journal, pour la liste de la regie : le type, l'heure, et le nom de la personne. */
export const recentEventSchema = z.object({
  type: z.string(),
  survenuLe: z.string(),
  nom: z.string().nullable(),
});
export type RecentEvent = z.infer<typeof recentEventSchema>;

/**
 * Reponse de `GET /api/twitch/etat`. Un role absent de `comptes` n'est pas encore autorise.
 *
 * - `runtime` : le travail de fond, ou `null` s'il n'existe pas dans ce processus ;
 * - `stream` : le direct ouvert (`debut`, date ISO 8601), ou `null` ;
 * - `presents` : le nombre de personnes comptees dans le stream ouvert (celles qui ont ecrit, ou
 *   sont restees dix minutes), 0 sans stream ouvert ;
 * - `derniers` : les dix derniers evenements, du plus recent au plus ancien (pas les messages) ;
 * - `journalLisible` : faux quand la lecture du journal a echoue. `stream`, `presents` et `derniers`
 *   sont alors vides, et ne disent rien de ce qui est reellement en base. Vrai quand rien n'a ete
 *   lu parce que Twitch n'est pas configure.
 */
export const etatSchema = z.object({
  configure: z.boolean(),
  manquantes: z.array(z.string()),
  comptes: z.array(accountStatusSchema),
  runtime: runtimeStatusSchema.nullable(),
  stream: z.object({ debut: z.string() }).nullable(),
  presents: z.number().int().nonnegative(),
  derniers: z.array(recentEventSchema),
  journalLisible: z.boolean(),
});
export type TwitchEtat = z.infer<typeof etatSchema>;

/**
 * Ce que `POST /api/twitch/essai-bot` repond : jamais un texte de Twitch.
 * - `envoye` : Twitch a accepte le message, le bot a parle ;
 * - `refuse` : Twitch a repondu que le message n'est pas parti ;
 * - `indisponible` : pas de travail de fond actif, compte du bot absent ou a reautoriser, erreur,
 *   ou un essai fait il y a moins de dix secondes.
 */
export const ESSAI_BOT_RESULTS = ["envoye", "refuse", "indisponible"] as const;
export type EssaiBotResult = (typeof ESSAI_BOT_RESULTS)[number];

export const essaiBotSchema = z.object({ resultat: z.enum(ESSAI_BOT_RESULTS) });

/**
 * Le corps de `POST /api/twitch/opposition` : la personne qui s'oppose, designee par son login Twitch
 * OU par son identifiant Twitch (il en faut un des deux ; l'identifiant l'emporte). Le login peut
 * porter un `@` devant, que la route retire.
 */
const LOGIN_FORME = /^@?[A-Za-z0-9_]{1,25}$/;
const IDENTIFIANT_FORME = /^[0-9]{1,20}$/;
export const oppositionRequeteSchema = z
  .object({
    login: z.string().trim().max(40).optional(),
    twitchUserId: z.string().trim().max(40).optional(),
  })
  .superRefine((corps, contexte) => {
    const login = corps.login ?? "";
    const identifiant = corps.twitchUserId ?? "";
    if (login === "" && identifiant === "") {
      contexte.addIssue({
        code: "custom",
        message: "un login ou un identifiant Twitch est requis",
      });
    }
    if (login !== "" && !LOGIN_FORME.test(login)) {
      contexte.addIssue({ code: "custom", path: ["login"], message: "login Twitch illisible" });
    }
    if (identifiant !== "" && !IDENTIFIANT_FORME.test(identifiant)) {
      contexte.addIssue({
        code: "custom",
        path: ["twitchUserId"],
        message: "identifiant Twitch illisible (des chiffres)",
      });
    }
  });
export type OppositionRequete = z.infer<typeof oppositionRequeteSchema>;

/**
 * Ce que `POST /api/twitch/opposition` repond, jamais un identifiant ni un pseudo :
 * - `efface` : la personne etait connue, ses donnees sont effacees et elle ne sera plus notee ;
 * - `inscrit` : elle n'etait pas connue (ou plus), elle ne sera pas notee non plus ;
 * - `identifiant_necessaire` : un login seul, et personne de ce login n'a ete vu : rien n'est fait,
 *   il faut l'identifiant Twitch (un login ne dit pas qui sera la demain) ;
 * - `plusieurs` : plusieurs personnes ont porte ce login, rien n'est fait : l'identifiant Twitch seul
 *   dit laquelle.
 */
export const OPPOSITION_RESULTS = [
  "efface",
  "inscrit",
  "identifiant_necessaire",
  "plusieurs",
] as const;
export type OppositionResult = (typeof OPPOSITION_RESULTS)[number];

export const oppositionReponseSchema = z.object({ resultat: z.enum(OPPOSITION_RESULTS) });

/**
 * Le corps de `POST /api/twitch/opposition/lever` : l'identifiant Twitch de la personne dont Nicolas leve l'opposition
 * (des chiffres). Un login ne suffit pas : `oppositions` ne garde que l'identifiant, jamais un login.
 */
export const oppositionLeveeRequeteSchema = z.object({
  twitchUserId: z
    .string()
    .trim()
    .regex(IDENTIFIANT_FORME, "identifiant Twitch illisible (des chiffres)"),
});
export type OppositionLeveeRequete = z.infer<typeof oppositionLeveeRequeteSchema>;

/**
 * Ce que `POST /api/twitch/opposition/lever` repond, jamais un identifiant ni un pseudo :
 * - `levee` : la personne avait une opposition, elle est retiree ; elle peut de nouveau rejoindre, et le journal peut de
 *   nouveau la noter. Ce qui a ete efface ne revient pas ;
 * - `absente` : elle n'avait pas d'opposition (ou elle est deja levee) : rien n'a change.
 */
export const OPPOSITION_LEVEE_RESULTS = ["levee", "absente"] as const;
export type OppositionLeveeResult = (typeof OPPOSITION_LEVEE_RESULTS)[number];

export const oppositionLeveeReponseSchema = z.object({
  resultat: z.enum(OPPOSITION_LEVEE_RESULTS),
});
